/**
 * Training videos from Storage (training/<id>.mp4|vtt|jpg — signed-in users, per storage.rules). The download URLs
 * are fetched once per session and shared by the help page and the "?" dialog.
 */
import { useEffect, useState } from 'react';
import { getDownloadURL, ref } from 'firebase/storage';
import { storage } from '@/lib/firebase';
import { usePermissions } from '@/auth/usePermissions';
import { TRAINING_VIDEOS, trainingFile, type TrainingVideo } from '@shared/training.ts';

export interface VideoUrls { mp4: string; vtt: string | null; jpg: string | null }
type State = { status: 'loading' } | { status: 'ready'; urls: VideoUrls } | { status: 'missing' };

const cache = new Map<string, Promise<VideoUrls | null>>();
const optional = (p: Promise<string>) => p.catch(() => null);

function load(v: TrainingVideo): Promise<VideoUrls | null> {
  let p = cache.get(v.id);
  if (!p) {
    p = getDownloadURL(ref(storage, trainingFile(v.id, 'mp4')))
      .then(async (mp4) => ({ mp4, vtt: await optional(getDownloadURL(ref(storage, trainingFile(v.id, 'vtt')))), jpg: await optional(getDownloadURL(ref(storage, trainingFile(v.id, 'jpg')))) }))
      .catch(() => { cache.delete(v.id); return null; }); // not uploaded yet (or no access) — try again next time
    cache.set(v.id, p);
  }
  return p;
}

export function useTrainingVideo(v: TrainingVideo | null): State {
  const [s, setS] = useState<State>({ status: 'loading' });
  useEffect(() => {
    if (!v) { setS({ status: 'missing' }); return; }
    let live = true;
    setS({ status: 'loading' });
    void load(v).then((urls) => { if (live) setS(urls ? { status: 'ready', urls } : { status: 'missing' }); });
    return () => { live = false; };
  }, [v]);
  return s;
}

/** The videos this user may watch (the same mapping as storage.rules). */
export function useAllowedVideos(): TrainingVideo[] {
  const { canRead } = usePermissions();
  return TRAINING_VIDEOS.filter((v) => !v.requires || canRead(v.requires));
}
